import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiConfigService } from '../services/api';
import '../styles/Login.css';

const Login = () => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: credenciais, 2: OTP
  const [tempToken, setTempToken] = useState('');
  const [ultimoAcesso, setUltimoAcesso] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyOTP } = useAuth();

  useEffect(() => {
    // Carregar logo quando o componente montar
    const url = apiConfigService.getLogoUrl();
    setLogoUrl(`${url}?t=${Date.now()}`);
  }, []);

  // Verificar se vem do setup OTP
  useEffect(() => {
    if (location.state?.showSecurityPopup) {
      // Login já foi completado no OTPSetup, só navegar pro dashboard
      navigate('/dashboard');
    }
  }, [location.state, navigate]);

  const handleStep1 = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!username || !password) {
      setError('Por favor, preencha todos os campos');
      setLoading(false);
      return;
    }

    try {
      const result = await login(username, password);
      
      console.log('🔑 Resultado do login no Login.js:', result);
      
      if (result.success) {
        console.log('✅ Login com sucesso, navegando para dashboard');
        navigate('/dashboard');
      } else if (result.requiresOTPSetup) {
        console.log('🔐 Requer setup de OTP, navegando para /otp/setup');
        // Primeiro acesso - configurar OTP
        localStorage.setItem('tempToken', result.tempToken);
        localStorage.setItem('tempUsername', username);
        navigate('/otp/setup');
      } else if (result.requiresOTP) {
        console.log('🔐 Requer verificação de OTP');
        // OTP já configurado - solicitar código
        setTempToken(result.tempToken);
        setUltimoAcesso({
          ip: result.ultimo_ip_login,
          data: result.ultimo_login_at
        });
        setStep(2);
      } else {
        console.log('❌ Erro no login:', result.error);
        setError(result.error);
      }
    } catch (err) {
      console.error('❌ Erro ao fazer login:', err);
      setError('Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!otpCode || otpCode.length !== 6) {
      setError('Digite um código de 6 dígitos');
      setLoading(false);
      return;
    }

    try {
      const result = await verifyOTP(tempToken, otpCode);
      
      if (result.success) {
        // Salvar informações de segurança para o popup
        localStorage.setItem('showSecurityPopup', 'true');
        navigate('/dashboard');
      } else {
        setError(result.error || 'Código inválido');
      }
    } catch (err) {
      setError('Erro ao verificar OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          {logoUrl && (
            <div className="login-logo">
              <img 
                src={logoUrl} 
                alt="Logo" 
                onError={() => setLogoUrl(null)}
              />
            </div>
          )}
          {!logoUrl && (
            <>
              <h1>DVS TELECOM</h1>
              <p>Sistema de Vendas CRM</p>
            </>
          )}
        </div>

        {step === 1 ? (
          <form onSubmit={handleStep1} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Usuário</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Digite seu usuário"
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Senha</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            <button 
              type="submit" 
              className="btn-login"
              disabled={loading}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleStep2} className="login-form">
            <div className="form-group">
              <label htmlFor="otpCode">Código de Autenticação</label>
              <input
                id="otpCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="6"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                disabled={loading}
                autoFocus
                className="otp-input"
              />
              <small>Digite o código de 6 dígitos do Google Authenticator</small>
            </div>

            {ultimoAcesso?.ip && (
              <div className="security-info" style={{ 
                background: 'rgba(0, 102, 255, 0.1)',
                border: '1px solid var(--azul-dvs)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <p style={{ 
                  color: 'var(--texto-secundario)',
                  fontSize: '0.875rem',
                  margin: '0 0 0.5rem 0'
                }}>
                  Último acesso:
                </p>
                <p style={{ 
                  color: 'var(--texto-primario)',
                  fontSize: '0.9rem',
                  margin: 0,
                  fontWeight: 600
                }}>
                  🌐 IP: {ultimoAcesso.ip || 'N/A'}
                  {ultimoAcesso.data && (
                    <>
                      <br />
                      📅 {new Date(ultimoAcesso.data).toLocaleString('pt-BR')}
                    </>
                  )}
                </p>
              </div>
            )}

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            <button 
              type="submit" 
              className="btn-login"
              disabled={loading}
            >
              {loading ? 'Verificando...' : '🔐 Verificar'}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep(1);
                setTempToken('');
                setOtpCode('');
                setUltimoAcesso(null);
              }}
              className="btn-login"
              style={{ 
                background: 'transparent',
                border: '1px solid var(--border-color)',
                marginTop: '0.5rem'
              }}
              disabled={loading}
            >
              ← Voltar
            </button>
          </form>
        )}

        <div className="login-footer">
          <p>© 2026 DVStelecom. Todos os direitos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
